<template>
  <v-dialog
    :value="value"
    max-width="720"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>
          <v-icon class="mr-1">
            {{ icons.mdiFileTree }}
          </v-icon>
          関係性ツリー
        </span>
        <v-btn
          icon
          @click="$emit('input', false)"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text style="max-height: 60vh; overflow-y: auto;">
        <v-skeleton-loader
          v-if="isLoading"
          type="list-item@3"
        />

        <template v-else-if="rootNode">
          <div class="rtree-wrapper">
            <ul class="rtree">
              <CustomerReferralTreeNode
                :node="rootNode"
                :is-root="true"
              />
            </ul>
          </div>

          <div class="text-caption grey--text mt-2">
            金額は各顧客の確定売上です
          </div>
        </template>

        <div
          v-else
          class="text-center grey--text py-4"
        >
          紹介関係がありません
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, watch, getCurrentInstance } from '@vue/composition-api'
import { mdiFileTree, mdiClose } from '@mdi/js'
import CustomerReferralApi from '@/api/waiter/CustomerReferral'
import { handleApiError } from '@/utils/errorHandler'
import CustomerReferralTreeNode from './CustomerReferralTreeNode.vue'

export default {
  name: 'CustomerReferralTreeDialog',
  components: {
    CustomerReferralTreeNode,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    customer: {
      type: Object,
      default: null,
    },
    // 集計期間（指定時は各ノードの確定売上を期間で絞る）
    startDate: {
      type: String,
      default: null,
    },
    endDate: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy

    const rootNode = ref(null)
    const isLoading = ref(false)

    const fetchTree = async () => {
      if (!props.customer) return

      isLoading.value = true
      rootNode.value = null
      try {
        const res = await CustomerReferralApi.getReferralTreeFull(props.customer.id, {
          startDate: props.startDate,
          endDate: props.endDate,
        })
        rootNode.value = res.data.referralTree || null
      } catch (error) {
        handleApiError(error, { $toast: vm.$toast })
      } finally {
        isLoading.value = false
      }
    }

    watch(() => props.value, isOpen => {
      if (isOpen) fetchTree()
    })

    return {
      rootNode,
      isLoading,
      icons: {
        mdiFileTree,
        mdiClose,
      },
    }
  },
}
</script>

<style scoped>
/* ディシジョンツリー風レイアウト（ライブラリ不使用・CSSのみ）
   ノードはflexで横並びにし、擬似要素のボーダーで接続線を描く */
.rtree-wrapper {
  overflow-x: auto;
  padding-bottom: 8px;
}

.rtree-wrapper ::v-deep ul {
  position: relative;
  display: flex;
  justify-content: center;
  margin: 0;
  padding: 20px 0 0;
  list-style: none;
}

/* 親カードから子リストへ降りる縦線 */
.rtree-wrapper ::v-deep ul.rtree-children::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  width: 0;
  height: 20px;
  border-left: 1px solid rgba(128, 128, 128, 0.5);
}

.rtree-wrapper ::v-deep li.rtree-node {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 6px 0;
}

/* 兄弟ノードをつなぐ横線（左半分）と各ノードへ降りる縦線（右半分+縦線） */
.rtree-wrapper ::v-deep li.rtree-node::before,
.rtree-wrapper ::v-deep li.rtree-node::after {
  content: '';
  position: absolute;
  top: 0;
  right: 50%;
  width: 50%;
  height: 20px;
  border-top: 1px solid rgba(128, 128, 128, 0.5);
}

.rtree-wrapper ::v-deep li.rtree-node::after {
  right: auto;
  left: 50%;
  border-left: 1px solid rgba(128, 128, 128, 0.5);
}

/* 一人っ子は横線不要。親からの縦線が直接つながる */
.rtree-wrapper ::v-deep li.rtree-node:only-child::before,
.rtree-wrapper ::v-deep li.rtree-node:only-child::after {
  display: none;
}

.rtree-wrapper ::v-deep li.rtree-node:only-child {
  padding-top: 0;
}

/* 端のノードは外側へ伸びる横線を消し、角を丸める */
.rtree-wrapper ::v-deep li.rtree-node:first-child::before,
.rtree-wrapper ::v-deep li.rtree-node:last-child::after {
  border: 0 none;
}

.rtree-wrapper ::v-deep li.rtree-node:last-child::before {
  border-right: 1px solid rgba(128, 128, 128, 0.5);
  border-radius: 0 5px 0 0;
}

.rtree-wrapper ::v-deep li.rtree-node:first-child::after {
  border-radius: 5px 0 0 0;
}

/* ノードカード */
.rtree-wrapper ::v-deep .rtree-card {
  min-width: 90px;
  padding: 6px 10px;
  text-align: center;
  border: 1px solid rgba(128, 128, 128, 0.5);
  border-radius: 6px;
}

.rtree-wrapper ::v-deep .rtree-card--root {
  border-color: var(--v-primary-base);
}

.rtree-wrapper ::v-deep .rtree-name {
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
}
</style>
